import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiProhibitBold, PiWarningBold } from 'react-icons/pi';
import { Button, Skeleton, Switch } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { useI18n } from '@v2/providers/I18nProvider';
import { invalidateDealCommissionQueries } from '@v2/features/ploomes-deals/detail/invalidateDealCommissionQueries';

type Action = 'cancel' | 'clawback';
type PaidReason = 'paid' | 'past_competence' | 'closed_month' | 'created_after_month' | 'open';

type PreviewRow = {
    commission_record_id: number;
    commission_installment_id?: number | null;
    user_id: number;
    user_name?: string | null;
    commission_role: string;
    competence_month?: string | null;
    amount: number;
    action: Action;
    paid_reason?: PaidReason;
};

type InstallmentInfo = { id: number; label: string; due_date: string | null; amount: number | null; status: string | null };

type SaleCancellation = {
    target_competence: string;
    installments?: InstallmentInfo[];
    rows: PreviewRow[];
    cancel_total: number;
    clawback_total: number;
    cancelled_at?: string | null;
    cancel_reason?: string | null;
    can_plan_clawback?: boolean;
    plan_months?: string[];
    max_clawback_times?: number;
};

type Plan = { start: string; times: number };

/** Meses seguidos a partir de `start` (YYYY-MM). */
function monthsFrom(start: string, times: number): string[] {
    const [y, m] = start.split('-').map(Number);
    return Array.from({ length: times }, (_, i) => {
        const d = new Date(y, m - 1 + i, 1);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
}

/** Mesmo rateio do servidor: partes iguais por comissão, centavos que sobram no último mês. */
function splitAmount(amount: number, months: string[]): Record<string, number> {
    const cents = Math.round(amount * 100);
    const part = Math.floor(cents / months.length);
    return Object.fromEntries(months.map((mo, i) => [mo, (i === months.length - 1 ? cents - part * (months.length - 1) : part) / 100]));
}

type Scope = 'sale' | 'installments';

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(v ?? 0);

function fmtMonth(ym?: string | null) {
    if (!ym) return '—';
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, 1).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
}

const fmtDate = (d?: string | null) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('pt-BR') : null);

/** Ação sugerida como "já paga": trocar para cancelar mexe no relatório daquele mês. */
const PAID_REASONS: PaidReason[] = ['paid', 'past_competence', 'closed_month'];

export default function SaleCancellationCard({
    dealId,
    open: controlledOpen,
    onClose,
    hideTrigger = false,
}: {
    dealId: number | string;
    open?: boolean;
    onClose?: () => void;
    hideTrigger?: boolean;
}) {
    const { t } = useI18n();
    const qc = useQueryClient();
    const [innerOpen, setInnerOpen] = useState(false);
    const open = controlledOpen ?? innerOpen;
    const setOpen = (value: boolean) => {
        setInnerOpen(value);
        if (!value) onClose?.();
    };
    const [reason, setReason] = useState('');
    const [scope, setScope] = useState<Scope>('sale');
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [overrides, setOverrides] = useState<Record<number, Action>>({});
    const [plan, setPlan] = useState<Plan | null>(null);
    const [perUser, setPerUser] = useState(false);
    const [userPlans, setUserPlans] = useState<Record<number, Plan>>({});
    const queryKey = ['commissioning', 'sale-cancellation', dealId];

    const query = useQuery({
        queryKey,
        queryFn: () => api.get(`/commissioning/deals/${dealId}/sale-cancellation`).then((r) => r.data.data as SaleCancellation),
        enabled: open,
    });
    const data = query.data;
    const alreadyCancelled = !!data?.cancelled_at;

    // Uma seção por parcela, na ordem do cronograma; comissão sem parcela no fim.
    const groups = useMemo(() => {
        if (!data) return [];
        const info = new Map((data.installments ?? []).map((i) => [i.id, i]));
        const byId = new Map<number, PreviewRow[]>();
        for (const row of data.rows) {
            const id = row.commission_installment_id ?? 0;
            byId.set(id, [...(byId.get(id) ?? []), row]);
        }
        const order = (data.installments ?? []).map((i) => i.id).filter((id) => id !== 0);
        return [...order, 0]
            .filter((id) => byId.has(id))
            .map((id) => ({
                id,
                info: info.get(id) ?? { id, label: t('v2.commissioning.sale_cancel.no_installment'), due_date: null, amount: null, status: null },
                rows: byId.get(id)!,
            }));
    }, [data, t]);

    const futureIds = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        return groups.filter((g) => g.id !== 0 && (g.info.due_date ?? '') >= today).map((g) => g.id);
    }, [groups]);

    // Ao abrir: "só as futuras" já marcadas no modo de parcelas.
    useEffect(() => {
        if (!open) return;
        setSelected(new Set(futureIds));
        setOverrides({});
        setPlan(data?.plan_months?.[0] ? { start: data.plan_months[0], times: 1 } : null);
        setPerUser(false);
        setUserPlans({});
    }, [open, data]); // eslint-disable-line react-hooks/exhaustive-deps

    const actionOf = (row: PreviewRow): Action => overrides[row.commission_record_id] ?? row.action;
    const included = (groupId: number) => scope === 'sale' || selected.has(groupId);
    const totals = useMemo(() => {
        let cancel = 0;
        let clawback = 0;
        for (const g of groups) {
            if (!included(g.id)) continue;
            for (const row of g.rows) {
                if (actionOf(row) === 'clawback') clawback += row.amount;
                else cancel += row.amount;
            }
        }
        return { cancel: Math.round(cancel * 100) / 100, clawback: Math.round(clawback * 100) / 100 };
    }, [groups, scope, selected, overrides]); // eslint-disable-line react-hooks/exhaustive-deps

    // Quem tem estorno na seleção atual, com o desconto previsto mês a mês.
    const canPlan = !!data?.can_plan_clawback && !!plan && !alreadyCancelled;
    const openMonths = useMemo(() => new Set(data?.plan_months ?? []), [data]);
    const planFor = (userId: number): Plan => (perUser && userPlans[userId]) || plan || { start: data?.target_competence ?? '', times: 1 };
    const clawbackPeople = useMemo(() => {
        const people = new Map<number, { name: string; total: number; rows: PreviewRow[] }>();
        for (const g of groups) {
            if (!included(g.id)) continue;
            for (const row of g.rows) {
                if (actionOf(row) !== 'clawback') continue;
                const p = people.get(row.user_id) ?? { name: row.user_name ?? '—', total: 0, rows: [] };
                p.total = Math.round((p.total + row.amount) * 100) / 100;
                p.rows.push(row);
                people.set(row.user_id, p);
            }
        }
        return [...people.entries()].map(([userId, p]) => {
            const pl = planFor(userId);
            const months = monthsFrom(pl.start, pl.times);
            const byMonth: Record<string, number> = {};
            for (const row of p.rows) {
                for (const [mo, v] of Object.entries(splitAmount(row.amount, months))) byMonth[mo] = Math.round(((byMonth[mo] ?? 0) + v) * 100) / 100;
            }
            return { userId, name: p.name, total: p.total, plan: pl, byMonth, blocked: months.some((mo) => !openMonths.has(mo)) };
        });
    }, [groups, scope, selected, overrides, plan, perUser, userPlans, openMonths]); // eslint-disable-line react-hooks/exhaustive-deps
    const planBlocked = canPlan && clawbackPeople.some((p) => p.blocked);

    const cancel = useMutation({
        mutationFn: () => {
            const visibleRows = groups.filter((g) => included(g.id)).flatMap((g) => g.rows);
            const actions = Object.fromEntries(
                visibleRows.filter((r) => overrides[r.commission_record_id] && overrides[r.commission_record_id] !== r.action)
                    .map((r) => [r.commission_record_id, overrides[r.commission_record_id]]),
            );
            return api.post(`/commissioning/deals/${dealId}/sale-cancellation`, {
                reason: reason.trim(),
                scope,
                installment_ids: scope === 'installments' ? [...selected] : undefined,
                actions,
                clawback_plan: canPlan && plan
                    ? {
                          ...plan,
                          users: perUser
                              ? Object.fromEntries(clawbackPeople.map((p) => [p.userId, p.plan]))
                              : undefined,
                      }
                    : undefined,
            });
        },
        onSuccess: (res) => {
            toast.success(res.data?.message ?? t('v2.commissioning.sale_cancel.done', { month: '' }));
            setOpen(false);
            setReason('');
            void qc.invalidateQueries({ queryKey });
            void invalidateDealCommissionQueries(qc, dealId);
        },
        onError: (err) => {
            const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
            toast.error(message ?? t('v2.commissioning.sale_cancel.already'));
        },
    });

    const toggleGroup = (id: number, on: boolean) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (on) next.add(id);
            else next.delete(id);
            return next;
        });

    const toggleAction = (row: PreviewRow) =>
        setOverrides((prev) => {
            const next = { ...prev };
            const flipped: Action = actionOf(row) === 'cancel' ? 'clawback' : 'cancel';
            if (flipped === row.action) delete next[row.commission_record_id];
            else next[row.commission_record_id] = flipped;
            return next;
        });

    const canConfirm = !!data && reason.trim().length >= 3 && (scope === 'sale' || selected.size > 0) && !planBlocked;
    const maxTimes = data?.max_clawback_times ?? 12;
    const selectCls = 'h-8 rounded-lg border border-gray-200 bg-white px-2 text-xs dark:border-zinc-700 dark:bg-zinc-900';
    const planInputs = (value: Plan, onChange: (p: Plan) => void) => (
        <span className="inline-flex items-center gap-1.5">
            <select value={value.start} onChange={(e) => onChange({ ...value, start: e.target.value })} className={selectCls} aria-label={t('v2.commissioning.sale_cancel.plan_start')}>
                {(data?.plan_months ?? []).map((mo) => (
                    <option key={mo} value={mo}>{fmtMonth(mo)}</option>
                ))}
            </select>
            <select value={value.times} onChange={(e) => onChange({ ...value, times: Number(e.target.value) })} className={selectCls} aria-label={t('v2.commissioning.sale_cancel.plan_times')}>
                {Array.from({ length: maxTimes }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n}x</option>
                ))}
            </select>
        </span>
    );

    return (
        <>
            {!hideTrigger && <div className="flex justify-end">
                <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)}>
                    <PiProhibitBold className="mr-1 size-3.5" aria-hidden />
                    {t('v2.commissioning.sale_cancel.button')}
                </Button>
            </div>}

            <V2Modal
                open={open}
                onClose={() => setOpen(false)}
                title={t('v2.commissioning.sale_cancel.title')}
                description={data ? t('v2.commissioning.sale_cancel.hint', { month: fmtMonth(data.target_competence) }) : undefined}
                size="2xl"
                footer={(
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                            {t('v2.commissioning.director_approval.close')}
                        </Button>
                        {!alreadyCancelled && (
                            <Button
                                type="button"
                                color="danger"
                                isLoading={cancel.isPending}
                                disabled={!canConfirm}
                                onClick={() => cancel.mutate()}
                            >
                                {scope === 'installments'
                                    ? t('v2.commissioning.sale_cancel.confirm_installments')
                                    : t('v2.commissioning.sale_cancel.confirm')}
                            </Button>
                        )}
                    </div>
                )}
            >
                {query.isLoading && <Skeleton className="h-32 w-full rounded-xl" />}
                {data && (
                    <div className="space-y-3">
                        {alreadyCancelled && (
                            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
                                <span className="font-bold">{t('v2.commissioning.sale_cancel.cancelled_badge')}</span>
                                {data.cancel_reason ? ` · ${data.cancel_reason}` : ''}
                            </div>
                        )}

                        {!alreadyCancelled && (
                            <div className="space-y-1.5">
                                <div className="inline-flex rounded-lg bg-gray-100 p-0.5 text-xs font-semibold dark:bg-zinc-800">
                                    {(['sale', 'installments'] as const).map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            onClick={() => setScope(s)}
                                            className={cn(
                                                'rounded-md px-3 py-1.5 transition',
                                                scope === s
                                                    ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-100'
                                                    : 'text-gray-500 hover:text-gray-800 dark:text-zinc-400',
                                            )}
                                        >
                                            {t(`v2.commissioning.sale_cancel.scope_${s}`)}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-[11px] text-gray-500">{t(`v2.commissioning.sale_cancel.scope_${scope}_hint`)}</p>
                                {scope === 'installments' && (
                                    <div className="flex flex-wrap gap-1.5 text-[11px]">
                                        {[
                                            ['select_future', () => setSelected(new Set(futureIds))],
                                            ['select_all', () => setSelected(new Set(groups.map((g) => g.id)))],
                                            ['select_none', () => setSelected(new Set())],
                                        ].map(([key, fn]) => (
                                            <button
                                                key={key as string}
                                                type="button"
                                                onClick={fn as () => void}
                                                className="rounded-full border border-gray-200 px-2.5 py-0.5 font-medium text-gray-600 hover:border-primary hover:text-primary dark:border-zinc-700 dark:text-zinc-300"
                                            >
                                                {t(`v2.commissioning.sale_cancel.${key as string}`)}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {groups.length === 0 ? (
                            <p className="text-xs text-gray-500">{t('v2.commissioning.sale_cancel.empty')}</p>
                        ) : (
                            <div className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
                                {groups.map((g) => {
                                    const on = included(g.id);
                                    return (
                                        <section
                                            key={g.id}
                                            className={cn(
                                                'rounded-xl border transition',
                                                on ? 'border-gray-200 dark:border-zinc-700' : 'border-dashed border-gray-200 opacity-50 dark:border-zinc-800',
                                            )}
                                        >
                                            <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-gray-100 px-3 py-2 dark:border-zinc-800">
                                                {scope === 'installments' && !alreadyCancelled && (
                                                    <Switch
                                                        size="sm"
                                                        checked={on}
                                                        onChange={(e) => toggleGroup(g.id, e.target.checked)}
                                                        aria-label={g.info.label}
                                                        className="shrink-0 [&_label]:my-0"
                                                    />
                                                )}
                                                <span className="text-sm font-bold text-gray-900 dark:text-zinc-100">{g.info.label}</span>
                                                {g.info.due_date && (
                                                    <span className="text-[11px] text-gray-500">
                                                        {t('v2.commissioning.sale_cancel.installment_due', { date: fmtDate(g.info.due_date) ?? '' })}
                                                    </span>
                                                )}
                                                {g.info.amount != null && (
                                                    <span className="ml-auto text-[11px] tabular-nums text-gray-500">{brl(g.info.amount)}</span>
                                                )}
                                            </header>
                                            <table className="w-full text-left text-xs">
                                                <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/60">
                                                    {g.rows.map((row) => {
                                                        const action = actionOf(row);
                                                        const changed = action !== row.action;
                                                        const pastCancel = action === 'cancel' && PAID_REASONS.includes(row.paid_reason ?? 'open');
                                                        return (
                                                            <tr key={row.commission_record_id}>
                                                                <td className="px-3 py-1.5">
                                                                    <span className="font-semibold">{row.user_name ?? '—'}</span>
                                                                    <span className="ml-1 text-gray-500">{row.commission_role}</span>
                                                                </td>
                                                                <td className="px-2 py-1.5 capitalize text-gray-600 dark:text-zinc-300">
                                                                    {fmtMonth(row.competence_month)}
                                                                </td>
                                                                <td className="px-2 py-1.5 text-right tabular-nums">{brl(row.amount)}</td>
                                                                <td className="px-3 py-1.5 text-right">
                                                                    <button
                                                                        type="button"
                                                                        disabled={alreadyCancelled || !on}
                                                                        onClick={() => toggleAction(row)}
                                                                        title={row.paid_reason ? t(`v2.commissioning.sale_cancel.paid_reason_${row.paid_reason}`) : undefined}
                                                                        className={cn(
                                                                            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 transition disabled:cursor-default',
                                                                            action === 'clawback'
                                                                                ? 'bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-800'
                                                                                : 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-200 dark:ring-rose-800',
                                                                            !alreadyCancelled && on && 'hover:ring-2',
                                                                        )}
                                                                    >
                                                                        {action === 'clawback'
                                                                            ? t('v2.commissioning.sale_cancel.action_clawback')
                                                                            : t('v2.commissioning.sale_cancel.action_cancel')}
                                                                        {changed && <span className="font-normal opacity-70">· {t('v2.commissioning.sale_cancel.action_changed')}</span>}
                                                                    </button>
                                                                    {row.paid_reason && (
                                                                        <span className={cn('mt-0.5 block text-[10px]', pastCancel ? 'text-rose-600' : 'text-gray-400')}>
                                                                            {pastCancel && <PiWarningBold className="mr-0.5 inline size-3" />}
                                                                            {pastCancel
                                                                                ? t('v2.commissioning.sale_cancel.past_cancel_warning')
                                                                                : t(`v2.commissioning.sale_cancel.paid_reason_${row.paid_reason}`)}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </section>
                                    );
                                })}
                            </div>
                        )}

                        <div className="grid gap-2 sm:grid-cols-2">
                            <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-zinc-800/60">
                                <p className="text-[10px] font-semibold uppercase text-gray-500">{t('v2.commissioning.sale_cancel.cancel_total')}</p>
                                <p className="text-sm font-bold tabular-nums">{brl(alreadyCancelled ? data.cancel_total : totals.cancel)}</p>
                            </div>
                            <div className="rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-950/30">
                                <p className="text-[10px] font-semibold uppercase text-amber-700">{t('v2.commissioning.sale_cancel.clawback_total')}</p>
                                <p className="text-sm font-bold tabular-nums text-amber-900 dark:text-amber-100">
                                    {brl(alreadyCancelled ? data.clawback_total : totals.clawback)}
                                </p>
                            </div>
                        </div>

                        {canPlan && clawbackPeople.length > 0 && (
                            <section className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900 dark:bg-amber-950/20">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                        <p className="text-xs font-bold text-amber-900 dark:text-amber-100">{t('v2.commissioning.sale_cancel.plan_title')}</p>
                                        <p className="text-[11px] text-amber-800/80 dark:text-amber-200/80">{t('v2.commissioning.sale_cancel.plan_hint')}</p>
                                    </div>
                                    {!perUser && plan && planInputs(plan, setPlan)}
                                </div>
                                <div className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-zinc-300">
                                    <Switch
                                        size="sm"
                                        checked={perUser}
                                        onChange={(e) => {
                                            setPerUser(e.target.checked);
                                            if (e.target.checked && plan) {
                                                setUserPlans(Object.fromEntries(clawbackPeople.map((p) => [p.userId, userPlans[p.userId] ?? plan])));
                                            }
                                        }}
                                        aria-label={t('v2.commissioning.sale_cancel.plan_per_user')}
                                        className="shrink-0 [&_label]:my-0"
                                    />
                                    {t('v2.commissioning.sale_cancel.plan_per_user')}
                                </div>
                                <ul className="space-y-1.5">
                                    {clawbackPeople.map((p) => (
                                        <li key={p.userId} className="rounded-lg bg-white/80 px-2.5 py-1.5 dark:bg-zinc-900/60">
                                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                                <span className="font-semibold">{p.name}</span>
                                                <span className="tabular-nums text-gray-500">{brl(p.total)}</span>
                                                {perUser && (
                                                    <span className="ml-auto">
                                                        {planInputs(p.plan, (np) => setUserPlans((prev) => ({ ...prev, [p.userId]: np })))}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-1 flex flex-wrap gap-1">
                                                {Object.entries(p.byMonth).map(([mo, v]) => (
                                                    <span
                                                        key={mo}
                                                        className={cn(
                                                            'rounded-full px-2 py-0.5 text-[10px] tabular-nums ring-1',
                                                            openMonths.has(mo)
                                                                ? 'bg-amber-50 text-amber-900 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-100 dark:ring-amber-800'
                                                                : 'bg-rose-50 text-rose-700 ring-rose-300',
                                                        )}
                                                    >
                                                        <span className="capitalize">{fmtMonth(mo)}</span> · −{brl(v)}
                                                    </span>
                                                ))}
                                            </div>
                                            {p.blocked && (
                                                <p className="mt-1 text-[10px] text-rose-600">
                                                    {t('v2.commissioning.sale_cancel.plan_month_closed', { month: Object.keys(p.byMonth).find((mo) => !openMonths.has(mo)) ?? '' })}
                                                </p>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {!alreadyCancelled && (
                            <label className="block text-xs font-semibold text-gray-600">
                                {t('v2.commissioning.sale_cancel.reason')}
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={3}
                                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                                />
                            </label>
                        )}
                    </div>
                )}
            </V2Modal>
        </>
    );
}
